import { FloatingList, useListItem, useMergeRefs, useId } from '@floating-ui/react';
import React__default from 'react';
import { Icon } from '../icons/index.js';
import '../list/index.js';
import '../stack/index.js';
import { item, itemInner, itemContent, description, checkedIcon } from './list_box.css.js';
import { List } from '../list/list.js';
import { ListItem } from '../list/list_item.js';
import { Stack } from '../stack/stack.js';

const ListBox = React__default.forwardRef(function ListBox({ children, className, value, onSelect, elementsRef, labelsRef, activeIndex, render = 'full', data, ...rest }, ref) {
    const state = useListBox({ value, onSelect, activeIndex, render, data });
    if (state.render === 'shallow' || !elementsRef) {
        return React__default.createElement(ListBoxContext.Provider, { value: state }, children);
    }
    return (React__default.createElement(ListBoxContext.Provider, { value: state },
        React__default.createElement(FloatingList, { elementsRef: elementsRef, labelsRef: labelsRef },
            React__default.createElement(List, { gap: "xsmall", ...rest, role: "listbox", className: className, ref: ref }, children))));
});
const ShallowListBoxOption = ({ value, label }) => {
    useListBoxDataOption({ value, data: { value, label } });
    return null;
};
const FullListBoxOption = React__default.forwardRef(function FullListBoxOption({ value, label, description: description$1, onClick, onKeyDown, onFocus, ...rest }, externalRef) {
    const { value: selectedValue, selectItem, activeIndex } = useListBoxState();
    const { ref: listItemRef, index } = useListItem({ label });
    const selected = selectedValue === value;
    const active = activeIndex === index;
    const ref = useMergeRefs([externalRef, listItemRef]);
    const id = useId();
    const labelId = `${id}-label`;
    const descriptionId = `${id}-description`;
    const handleClick = React__default.useCallback((event) => {
        selectItem?.(value);
        onClick?.(event);
    }, [value, selectItem, onClick]);
    const handleKeyDown = React__default.useCallback((event) => {
        switch (event.key) {
            case 'Enter': {
                event.stopPropagation();
                selectItem?.(value);
            }
        }
        onKeyDown?.(event);
    }, [selectItem, value, onKeyDown]);
    const handleFocus = React__default.useCallback((event) => {
        event.stopPropagation();
        onFocus?.(event);
    }, [onFocus]);
    return (React__default.createElement(ListItem, { ...rest, ref: ref, role: "option", "aria-selected": selected ? 'true' : 'false', "aria-labelledby": labelId, "aria-describedby": description$1 ? descriptionId : undefined, className: item, tabIndex: active ? 0 : -1, onClick: handleClick, onKeyDown: handleKeyDown, onFocus: handleFocus },
        React__default.createElement(Stack, { wrap: false, className: itemInner, alignItems: "start" },
            React__default.createElement(Stack, { direction: "column", wrap: false, gap: "xsmall", className: itemContent },
                React__default.createElement("span", { className: "label", id: labelId }, label),
                description$1 && (React__default.createElement("span", { className: description, id: descriptionId }, description$1))),
            React__default.createElement(Icon, { name: "small-check", className: checkedIcon }))));
});
const ListBoxOption = React__default.forwardRef(function ListBoxOption(props, ref) {
    const { render } = useListBoxState();
    if (render === 'shallow') {
        return React__default.createElement(ShallowListBoxOption, { ...props });
    }
    return React__default.createElement(FullListBoxOption, { ...props, ref: ref });
});
const ListBoxContext = React__default.createContext(null);
const useListBox = ({ value, onSelect, activeIndex, render, data, }) => {
    return React__default.useMemo(() => ({
        value,
        selectItem: onSelect,
        activeIndex,
        render,
        data,
    }), [value, onSelect, activeIndex, render, data]);
};
const useListBoxState = () => {
    const context = React__default.useContext(ListBoxContext);
    if (context == null) {
        throw new Error('ListBox subcomponents must be wrapped in <ListBox />');
    }
    return context;
};
function dataMapToItemArray(map) {
    return Array.from(map.values());
}
const useListBoxData = ({ children }) => {
    const dataRef = React__default.useRef(new Map());
    const [data, setData] = React__default.useState(() => new Map());
    const render = (React__default.createElement(ListBoxDataProvider, { value: null, dataRef: dataRef, onDataChange: setData },
        React__default.createElement(ListBox, { render: "shallow" }, children)));
    const find = React__default.useCallback((value) => {
        if (value === null)
            return null;
        const itemsArray = dataMapToItemArray(data);
        return itemsArray.find((i) => i.value === value) ?? null;
    }, [data]);
    return {
        render,
        data,
        find,
    };
};
const ListBoxDataContext = React__default.createContext({
    value: null,
    add: () => undefined,
    remove: () => undefined,
});
function useListBoxDataOption({ value, data: itemData }) {
    const { add, remove } = React__default.useContext(ListBoxDataContext);
    React__default.useLayoutEffect(() => {
        add(value, itemData);
        return () => {
            remove(value);
        };
    }, [value, itemData, add, remove]);
}
const ListBoxDataProvider = ({ value, children, dataRef, onDataChange }) => {
    return (React__default.createElement(ListBoxDataContext.Provider, { value: React__default.useMemo(() => {
            return {
                value,
                add: (value, itemData) => {
                    dataRef.current.set(value, itemData);
                    onDataChange?.(new Map(dataRef.current));
                },
                remove: (value) => {
                    dataRef.current.delete(value);
                    onDataChange?.(new Map(dataRef.current));
                },
            };
        }, [value, dataRef, onDataChange]) }, children));
};

export { ListBox, ListBoxOption, useListBoxData };
